import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import type { UUID } from '@/utils/uuid';

/**
 * Fetches the feature count for each given provider ID in parallel.
 * Intended for providers that the catalog API has excluded (already installed),
 * so we can still show an accurate feature count in the "Add Connector" modal.
 *
 * Returns a Map<providerId, count>. Entries are absent until the query resolves.
 */
export function useProviderFeatureCounts(
  tenantId: UUID | undefined,
  providerIds: UUID[]
): Map<UUID, number> {
  const enabled = !!tenantId && providerIds.length > 0;

  const results = useQueries({
    queries: providerIds.map((providerId) => ({
      // Distinct key — avoids colliding with the infinite-query cache used by useListProviderFeatures
      queryKey: ['provider-feature-count', tenantId, providerId] as const,
      queryFn: () =>
        providerService.listProviderFeatures(tenantId!, providerId, {
          limit: 200,
        }),
      enabled,
    })),
  });

  return useMemo(() => {
    const map = new Map<UUID, number>();
    providerIds.forEach((providerId, i) => {
      const count = results[i]?.data?.items.length;
      if (count != null) map.set(providerId, count);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerIds, results]);
}
